import { DeviceDiscoveryEndpoints, type PingResponse } from '@scaleaq/sitecontrol-lib';
import { useMutation } from '@tanstack/react-query';
import { useAuth } from 'react-oidc-context';

import { API_NAMES } from '@/constants';

import { useGetApiUrl } from '../apiHooks/useGetApiUrl.ts';
import { fetchAuthApiGet } from '../fetchAuthApiGet.ts';
import { applyRouteParams } from '../withId/applyRouteParams.ts';

/**
 * Pings an IP address from the site network (`GET /networkdevice/ping/{ipAddress}`).
 *
 * A mutation rather than a query: each ping is an on-demand check whose answer should not be
 * cached, and a failure (400 for an invalid IP, or the service being down) should surface at once
 * instead of being retried. An unreachable device is a successful response with a non-`Success`
 * status, not an error.
 */
export const usePingNetworkDevice = () => {
  const auth = useAuth();
  const api = useGetApiUrl(API_NAMES.deviceDiscoveryApi);

  return useMutation<PingResponse, Error, string>({
    mutationFn: ipAddress =>
      fetchAuthApiGet<PingResponse>({
        api,
        route: applyRouteParams(DeviceDiscoveryEndpoints.ping, {
          ipAddress: encodeURIComponent(ipAddress),
        }),
        user: auth.user,
        identifier: ipAddress,
        returnJsonResult: true,
      }),
  });
};
